import './styles/index.css';
import { initI18n, t, getRoundName, getRoundDesc } from './i18n';
import { initTheme } from './theme';
import { registerScreen, navigate, getCurrentScreen } from './router';
import { resetGame, hasSavedGame, resumeSavedGame, loadSavedGame } from './game-state';
import { standardDecks } from '../data';
import { APP_VERSION } from '../shared/version';
import { getActiveSession, isConnected, connect as wsConnect, rejoinSession as wsRejoin, onServerEvent as wsOnServerEvent } from './ws-client';

// Screen renderers — single-device
import { renderLobby } from './screens/lobby';
import { renderSetup } from './screens/setup';
import { renderRoundInterstitial } from './screens/round-interstitial';
import { renderTurnWaiting } from './screens/turn-waiting';
import { renderTurnActive } from './screens/turn-active';
import { renderTurnEnd } from './screens/turn-end';
import { renderProtest } from './screens/protest';
import { renderRoundComplete } from './screens/round-complete';
import { renderGameEnd } from './screens/game-end';
import { renderCustomDeck } from './screens/custom-deck';
import { renderMidRoundTransition } from './screens/mid-round-transition';

// Screen renderers — multi-device
import { renderMultiNewGame, renderMultiJoinGame } from './screens/multi-lobby';
import {
  renderMultiSetup,
  renderMultiRoundInterstitial,
  renderMultiTurnWaiting,
  renderMultiTurnActive,
  renderMultiTurnEnd,
  renderMultiRoundComplete,
  renderMultiGameEnd,
  setInitialState,
} from './screens/multi-gameplay';

// Screen renderers — admin (secret)
import { renderAdminStats } from './screens/admin-stats';

// Initialize i18n (before theme so lang is set)
initI18n();

// Initialize theming (renders settings bar with lang + theme)
initTheme();

// Register all screens — single-device
registerScreen('menu', renderMainMenu);
registerScreen('lobby', renderLobby);
registerScreen('setup', renderSetup);
registerScreen('round-interstitial', renderRoundInterstitial);
registerScreen('turn-waiting', renderTurnWaiting);
registerScreen('turn-active', renderTurnActive);
registerScreen('turn-end', renderTurnEnd);
registerScreen('protest', renderProtest);
registerScreen('round-complete', renderRoundComplete);
registerScreen('game-end', renderGameEnd);
registerScreen('rules', renderRules);
registerScreen('join', renderMultiJoinGame);
registerScreen('custom-deck', renderCustomDeck);
registerScreen('mid-round-transition', renderMidRoundTransition);

// Register admin screen (secret)
registerScreen('admin-stats', renderAdminStats);

// Register all screens — multi-device
registerScreen('multi-new-game', renderMultiNewGame);
registerScreen('multi-join', renderMultiJoinGame);
registerScreen('multi-setup', renderMultiSetup);
registerScreen('multi-round-interstitial', renderMultiRoundInterstitial);
registerScreen('multi-turn-waiting', renderMultiTurnWaiting);
registerScreen('multi-turn-active', renderMultiTurnActive);
registerScreen('multi-turn-end', renderMultiTurnEnd);
registerScreen('multi-round-complete', renderMultiRoundComplete);
registerScreen('multi-game-end', renderMultiGameEnd);

// Re-render current screen when locale changes
// Guard: if in an active WebSocket session, re-render via state-aware path
// instead of calling navigate() which can lose multi-device session context

window.addEventListener('locale-changed', () => {
  const screen = getCurrentScreen();
  const isMultiScreen = screen.startsWith('multi-');

  if (isMultiScreen && isConnected()) {
    // Don't call navigate() — it would re-render from scratch and lose lobby state.
    // The multi-gameplay module will re-render with the new locale on next state-update.
    // For immediate visual update, trigger a re-render from the gameplay module.
    window.dispatchEvent(new CustomEvent('multi-rerender'));
    return;
  }

  navigate(screen);
});

// Start at main menu (or handle ?join= deep link)
const joinParam = new URLSearchParams(window.location.search).get('join');
if (joinParam && /^\d{4}$/.test(joinParam)) {
  // Clean the URL without reloading
  window.history.replaceState({}, '', window.location.pathname);
  // Auto-navigate to join screen with code pre-filled
  navigate('multi-join');
  // Pre-fill the code digits after a tick (DOM needs to render)
  setTimeout(() => {
    joinParam.split('').forEach((digit, i) => {
      const input = document.querySelector(`.code-digit[data-index="${i}"]`) as HTMLInputElement;
      if (input) input.value = digit;
    });
  }, 50);
} else {
  navigate('menu');
}

// --- Screen Renderers (inline for menu/rules/join) ---

function renderMainMenu(): void {
  const app = document.getElementById('app')!;
  const totalCards = standardDecks.reduce(
    (sum, deck) => sum + deck.cards.length, 0
  );
  const activeSession = getActiveSession();
  const savedGame = loadSavedGame();

  app.innerHTML = `
    <h1>${t('appTitle')}</h1>
    <p class="subtitle">${t('appSubtitle')}</p>

    <div class="stack">
      ${activeSession ? `
        <button class="btn btn-primary" id="btn-rejoin" style="border-color: var(--color-warning); background: var(--color-warning);">
          ${t('rejoinGame') || 'Rejoin Game'} (${activeSession.code})
        </button>
      ` : ''}
      ${savedGame ? `
        <button class="btn btn-primary" id="btn-resume-saved" style="border-color: var(--color-success, #2ecc71); background: var(--color-success, #2ecc71);">
          Resume Game (R${savedGame.currentRound}, ${savedGame.scores.A}-${savedGame.scores.B})
        </button>
      ` : ''}
      <button class="btn btn-primary" id="btn-single-device">${t('singleDevice')}</button>
      <button class="btn btn-primary" id="btn-multi-host">${t('hostMultiDevice')}</button>
      <button class="btn btn-secondary" id="btn-multi-join">${t('joinGame')}</button>
      <button class="btn btn-tertiary" id="btn-rules">${t('rules')}</button>
    </div>

    <div class="deck-info">
      ${t('decksAvailable', standardDecks.length)} &middot; ${t('cardsTotal', totalCards)}
    </div>

    <div id="version-info" style="position: fixed; bottom: 8px; left: 8px; font-size: 10px; opacity: 0.5; font-family: monospace;">
      FE: v${APP_VERSION} | BE: <span id="be-version">...</span>
    </div>
  `;

  document.getElementById('btn-single-device')?.addEventListener('click', () => {
    resetGame();
    navigate('lobby');
  });
  document.getElementById('btn-resume-saved')?.addEventListener('click', () => {
    const phase = resumeSavedGame();
    if (phase) {
      // Navigate to the correct screen based on restored phase
      navigate(phase as any);
    } else {
      // Failed to load — fall back to lobby
      navigate('lobby');
    }
  });
  document.getElementById('btn-multi-host')?.addEventListener('click', () => {
    navigate('multi-new-game');
  });
  document.getElementById('btn-multi-join')?.addEventListener('click', () => {
    navigate('multi-join');
  });
  document.getElementById('btn-rejoin')?.addEventListener('click', () => {
    if (activeSession) {
      rejoinGame(activeSession.code);
    }
  });
  document.getElementById('btn-rules')?.addEventListener('click', () => {
    navigate('rules');
  });

  // Secret: tap the title 7 times to open admin stats
  let tapCount = 0;
  let tapTimer: ReturnType<typeof setTimeout> | null = null;
  document.querySelector('h1')?.addEventListener('click', () => {
    tapCount++;
    if (tapTimer) clearTimeout(tapTimer);
    if (tapCount >= 7) {
      tapCount = 0;
      navigate('admin-stats');
      return;
    }
    tapTimer = setTimeout(() => { tapCount = 0; }, 2000);
  });

  // Fetch backend version
  fetchBackendVersion();
}

async function rejoinGame(code: string): Promise<void> {
  try {
    await wsConnect();

    const unsub = wsOnServerEvent((msg) => {
      if (msg.type === 'state-update') {
        unsub();
        // Seed the multi-gameplay module with the initial state before navigating
        setInitialState(msg.state);
        // Successfully rejoined — navigate to the appropriate multi-device screen
        navigate('multi-setup'); // multi-gameplay will route to correct phase
      } else if (msg.type === 'error') {
        unsub();
        // Session gone — clear stored session and go to menu
        navigate('menu');
      }
    });

    wsRejoin(code);
  } catch {
    // Can't connect — just go to menu
    navigate('menu');
  }
}

function fetchBackendVersion(): void {
  const wsUrl = import.meta.env.VITE_WS_ENDPOINT || 'ws://localhost:3001';
  try {
    const ws = new WebSocket(wsUrl);
    ws.onopen = () => { ws.send(JSON.stringify({ type: 'get-version' })); };
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'version-response') {
          const el = document.getElementById('be-version');
          if (el) el.textContent = `v${msg.version}`;
          ws.close();
        }
      } catch { /* ignore */ }
    };
    ws.onerror = () => {
      const el = document.getElementById('be-version');
      if (el) el.textContent = 'offline';
    };
    setTimeout(() => { if (ws.readyState !== WebSocket.CLOSED) ws.close(); }, 3000);
  } catch {
    const el = document.getElementById('be-version');
    if (el) el.textContent = 'err';
  }
}

function renderRules(): void {
  const app = document.getElementById('app')!;

  app.innerHTML = `
    <h1>${t('rulesTitle')}</h1>
    <p class="subtitle">${t('rulesSubtitle')}</p>

    <div class="stack-lg" style="text-align: left;">
      <div class="surface">
        <h3>${t('round1Title')}</h3>
        <p class="text-secondary">${t('round1Desc')}</p>
      </div>
      <div class="surface">
        <h3>${t('round2Title')}</h3>
        <p class="text-secondary">${t('round2Desc')}</p>
      </div>
      <div class="surface">
        <h3>${t('round3Title')}</h3>
        <p class="text-secondary">${t('round3Desc')}</p>
      </div>
      <div class="surface">
        <h3>${t('round4Title')}</h3>
        <p class="text-secondary">${t('round4Desc')}
          <span class="badge">${t('optional')}</span></p>
      </div>
    </div>

    <button class="btn btn-tertiary" id="btn-back"
      style="margin-top: var(--space-xl);">${t('backToMenu')}</button>
  `;

  document.getElementById('btn-back')?.addEventListener('click', () => {
    navigate('menu');
  });
}

// (Join game handled by multi-lobby module)
